import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Row,
  Col,
  Input,
  Table,
  Card,
  CardBody,
  Badge
} from 'reactstrap';
import { useListLeads } from 'views/CRM/Lead/hooks/useListLeads';
import { useRecordTypesByUser } from 'views/RecordType/hooks/useRecordTypesByUser';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';
import ReactLoader from 'components/Loader';
import RecordTypeSelection from 'views/RecordType/components/RecordTypeSelection';
import Pagination from 'components/Pagination';
import { FaCheckCircle } from 'react-icons/fa';

import { toast } from 'react-toastify';

export const LeadsSelection = ({
  conversation, // eslint-disable-line no-unused-vars
  onClose,
  onAdvance,
  selectedLeads: initialSelectedLeads = [],
  associations = null
}) => {
  const [selectedRecordTypeId, setSelectedRecordTypeId] = useState(null);
  const [selectedLead, setSelectedLead] = useState(
    initialSelectedLeads.length > 0 ? initialSelectedLeads[0] : null
  );
  const [activePage, setActivePage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const { recordTypes, isLoadingRecordTypes, getRecordTypesByUser } =
    useRecordTypesByUser(entitiesName.LEAD);

  const { leads, totalLeads, isLoadingLeads, getLeads } = useListLeads({
    recordTypeId: selectedRecordTypeId,
    sortConfig: { key: null, direction: null }
  });

  useEffect(() => {
    getRecordTypesByUser();
  }, [getRecordTypesByUser]);

  useEffect(() => {
    if (recordTypes && recordTypes.length > 0 && !selectedRecordTypeId) {
      setSelectedRecordTypeId(recordTypes[0].id);
    }
  }, [recordTypes, selectedRecordTypeId]);

  // Debounce do termo de busca
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset da página quando o termo de busca muda
  useEffect(() => {
    if (debouncedSearchTerm !== searchTerm) {
      setActivePage(1);
    }
  }, [debouncedSearchTerm, searchTerm]);

  useEffect(() => {
    if (selectedRecordTypeId) {
      handleSearch();
    }
  }, [selectedRecordTypeId, activePage, debouncedSearchTerm]);

  const handleSearch = useCallback(() => {
    if (selectedRecordTypeId) {
      const filters = debouncedSearchTerm
        ? { textQuery: debouncedSearchTerm }
        : {};
      getLeads(activePage, filters);
    }
  }, [selectedRecordTypeId, activePage, debouncedSearchTerm, getLeads]);

  const handleOnSelectedRecordType = useCallback((recordTypeId) => {
    setSelectedRecordTypeId(recordTypeId);
    setActivePage(1);
    setSelectedLead(null);
  }, []);

  // Função para verificar se um lead já está associado
  const isLeadAssociated = useCallback(
    (leadId) => {
      if (!associations?.data?.leadStatus) return false;
      const leadStatus = associations.data.leadStatus.find(
        (status) => status.leadId === leadId
      );
      return leadStatus?.isAssociated || false;
    },
    [associations]
  );

  const handleLeadSelection = (leadId) => {
    // Não permitir selecionar leads já associados
    if (isLeadAssociated(leadId)) {
      toast.warning('Este lead já está associado a esta conversa');
      return;
    }
    setSelectedLead(leadId);
  };

  const handleAdvance = () => {
    if (!selectedLead) {
      toast.warning('Selecione um lead para avançar');
      return;
    }
    const selectedLeadData = leads?.find((lead) => lead.id === selectedLead);
    onAdvance(
      [selectedLead],
      selectedRecordTypeId,
      selectedLeadData ? [selectedLeadData] : []
    );
  };

  if (isLoadingRecordTypes) {
    return (
      <Row className="justify-content-md-center">
        <ReactLoader />
      </Row>
    );
  }

  if (!selectedRecordTypeId) {
    return (
      <RecordTypeSelection
        recordTypes={recordTypes}
        handleOnSelectedRecordType={handleOnSelectedRecordType}
      />
    );
  }

  // Componente responsivo para exibir leads
  const LeadItem = ({ lead, isSelected, onSelect }) => {
    const isAssociated = isLeadAssociated(lead.id);
    const itemStyle = isAssociated
      ? { backgroundColor: '#f8f9fa', opacity: 0.7 }
      : {};

    return (
      <>
        {/* Versão Desktop - Tabela */}
        <tr className="d-none d-md-table-row" style={itemStyle}>
          <td
            className="text-center align-middle"
            style={{
              verticalAlign: 'middle !important',
              padding: '0.75rem',
              width: '50px'
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%'
              }}
            >
              {isAssociated ? (
                <FaCheckCircle
                  size={20}
                  color="#28a745"
                  title="Lead já associado"
                />
              ) : (
                <div
                  onClick={() => onSelect(lead.id)}
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    border: '2px solid #007bff',
                    backgroundColor: isSelected ? '#007bff' : 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {isSelected && (
                    <div
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: 'white'
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          </td>
          <td
            className="align-middle"
            style={{ verticalAlign: 'middle', padding: '0.75rem' }}
          >
            {lead.name || '-'}
            {isAssociated && (
              <Badge color="success" className="ms-2" size="sm">
                Associado
              </Badge>
            )}
          </td>
          <td
            className="align-middle"
            style={{ verticalAlign: 'middle', padding: '0.75rem' }}
          >
            {lead.email || '-'}
          </td>
          <td
            className="align-middle"
            style={{ verticalAlign: 'middle', padding: '0.75rem' }}
          >
            {lead.telephone || '-'}
          </td>
          <td
            className="align-middle"
            style={{ verticalAlign: 'middle', padding: '0.75rem' }}
          >
            {lead.leadStage?.name || '-'}
          </td>
        </tr>

        {/* Versão Mobile - Card */}
        <div className="d-block d-md-none mb-2">
          <Card style={itemStyle}>
            <CardBody className="p-3">
              <Row className="align-items-center">
                <Col xs="2">
                  {isAssociated ? (
                    <FaCheckCircle
                      size={20}
                      color="#28a745"
                      title="Lead já associado"
                    />
                  ) : (
                    <div
                      onClick={() => onSelect(lead.id)}
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        border: '2px solid #007bff',
                        backgroundColor: isSelected ? '#007bff' : 'transparent',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {isSelected && (
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: 'white'
                          }}
                        />
                      )}
                    </div>
                  )}
                </Col>
                <Col xs="10">
                  <div>
                    <strong>{lead.name || 'Nome não informado'}</strong>
                    {isAssociated && (
                      <Badge color="success" className="ms-2" size="sm">
                        Associado
                      </Badge>
                    )}
                  </div>
                  {lead.email && (
                    <div className="text-muted small">📧 {lead.email}</div>
                  )}
                  {lead.telephone && (
                    <div className="text-muted small">📞 {lead.telephone}</div>
                  )}
                  {lead.leadStage?.name && (
                    <div className="text-muted small">
                      🎯 {lead.leadStage.name}
                    </div>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </div>
      </>
    );
  };

  return (
    <div>
      <Row className="mb-3">
        <Col md={12}>
          <Input
            type="text"
            placeholder="Buscar leads por nome, telefone ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && searchTerm !== debouncedSearchTerm && (
            <small className="text-muted">Buscando...</small>
          )}
        </Col>
      </Row>

      {isLoadingLeads ? (
        <Row className="justify-content-md-center">
          <ReactLoader />
        </Row>
      ) : (
        <>
          {/* Cabeçalho informativo */}
          <div className="mb-3">
            <Row>
              <Col>
                <small className="text-muted">
                  Selecione um lead para continuar ({leads?.length || 0} leads
                  disponíveis)
                </small>
              </Col>
            </Row>
          </div>

          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {/* Tabela Desktop */}
            <Table
              responsive
              striped
              className="d-none d-md-table align-items-center"
            >
              <thead>
                <tr>
                  <th style={{ width: '50px' }} className="text-center">
                    Seleção
                  </th>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Telefone</th>
                  <th>Fase</th>
                </tr>
              </thead>
              <tbody>
                {leads?.length > 0 ? (
                  leads.map((lead) => (
                    <LeadItem
                      key={lead.id}
                      lead={lead}
                      isSelected={selectedLead === lead.id}
                      onSelect={handleLeadSelection}
                    />
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center">
                      Nenhum lead encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            {/* Cards Mobile */}
            <div className="d-block d-md-none">
              {leads?.length > 0 ? (
                leads.map((lead) => (
                  <LeadItem
                    key={lead.id}
                    lead={lead}
                    isSelected={selectedLead === lead.id}
                    onSelect={handleLeadSelection}
                  />
                ))
              ) : (
                <Card>
                  <CardBody className="text-center text-muted">
                    Nenhum lead encontrado
                  </CardBody>
                </Card>
              )}
            </div>
          </div>

          {leads?.length > 0 && (
            <Row className="mt-3">
              <Col md={12} className="d-flex justify-content-center">
                <Pagination
                  activePage={activePage}
                  itemsCountPerPage={10}
                  totalItemsCount={totalLeads}
                  pageRangeDisplayed={3}
                  onChange={setActivePage}
                />
              </Col>
            </Row>
          )}

          <Row className="mt-3">
            <Col xs={12} md={6} className="mb-2 mb-md-0">
              <span className="text-muted">
                {selectedLead
                  ? `Lead selecionado: ${
                      leads?.find((lead) => lead.id === selectedLead)?.name ||
                      'Nome não informado'
                    }`
                  : 'Nenhum lead selecionado'}
              </span>
            </Col>
            <Col xs={12} md={6} className="d-flex justify-content-end">
              <Button
                color="secondary"
                size="sm"
                className="me-2"
                onClick={onClose}
              >
                Cancelar
              </Button>
              <Button
                color="primary"
                size="sm"
                onClick={handleAdvance}
                disabled={!selectedLead}
              >
                Avançar
              </Button>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};
